import { useAppTheme, useIntl, useSettings } from '@capsicum/common';
import { BaseCheckboxInput, GenericDrawer, InfoTooltip } from '@capsicum/common-web';
import { WarningIcon } from '@capsicum/common-web/icons';
import { type ConfirmBidProps, useConfirmBid } from '@capsicum/features/fanshop/shared';
import { useCallback } from 'react';
import styled from 'styled-components';

export const ConfirmBid = ({
	bidAmountChz,
	bidAmountFiat,
	buyersPremiumChz,
	buyersPremiumFiat,
	buyersPremiumPercentage,
	chzCurrencyShortcode,
	closeModal,
	country,
	estimatedShippingCostChz,
	estimatedShippingCostFiat,
	fiatCurrencyShortcode,
	onBidSubmit,
	testID,
	totalChz,
	totalFiat,
	vatChz,
	vatFiat
}: ConfirmBidProps) => {
	const { buttonPressHandler, confirmBidDisabled, consent, getTableItems, setConsent, texts } = useConfirmBid({
		closeModal,
		country,
		onBidSubmit
	});
	const { $t } = useIntl();
	const { theme } = useAppTheme();
	const { settings } = useSettings();

	const Warning = () => (
		<S.StyledWarning>
			<S.StyledWaringIcon />
			<S.WarningText>{texts.warning}</S.WarningText>
		</S.StyledWarning>
	);

	const Rows = useCallback(
		({
			items
		}: {
			items: Array<{
				isBold?: boolean;
				price: string;
				priceChz: string;
				title: string;
				toolTipText?: string;
				toolTipTitle?: string;
			}>;
		}) => (
			<div>
				{items.map(({ isBold, price, priceChz, title, toolTipText, toolTipTitle }, index) => (
					<S.StyledRow key={`row-${index}`}>
						<S.TitleWrapper key={title}>
							<S.PriceTitle $isBold={isBold}>{title}</S.PriceTitle>
							{(toolTipTitle || toolTipText) && (
								<InfoTooltip
									contentPosition="bottom_right"
									size={16}
									triggerColor={theme.colors.icons.main.enabled}
								>
									<S.TooltipTitle>{toolTipTitle}</S.TooltipTitle>
									<S.TooltipText>{toolTipText}</S.TooltipText>
								</InfoTooltip>
							)}
						</S.TitleWrapper>
						<div>
							<S.Price>{price}</S.Price>
							<S.PriceChz>{priceChz}</S.PriceChz>
						</div>
					</S.StyledRow>
				))}
			</div>
		),
		[
			bidAmountFiat,
			bidAmountChz,
			fiatCurrencyShortcode,
			chzCurrencyShortcode,
			buyersPremiumFiat,
			buyersPremiumChz,
			buyersPremiumPercentage,
			estimatedShippingCostChz,
			estimatedShippingCostFiat,
			totalFiat,
			totalChz,
			vatChz,
			vatFiat
		]
	);

	const handlePress = (url: string) => {
		window?.open?.(url, '_blank')?.focus();
	};

	return (
		<GenericDrawer
			buttonDisabled={!consent || confirmBidDisabled}
			buttonText={texts.buttonText}
			testID={testID}
			title={texts.title}
			onButtonPress={buttonPressHandler}
		>
			<Warning />
			<Rows
				items={getTableItems({
					bidAmountChz,
					bidAmountFiat,
					buyersPremiumChz,
					buyersPremiumFiat,
					buyersPremiumPercentage,
					chzCurrencyShortcode,
					estimatedShippingCostChz,
					estimatedShippingCostFiat,
					fiatCurrencyShortcode,
					totalChz,
					totalFiat,
					vatChz,
					vatFiat
				})}
			/>
			<S.DescriptionText>{texts.description}</S.DescriptionText>
			<S.DeliveryToText>{texts.deliveryTo}</S.DeliveryToText>
			<S.CheckboxWrapper>
				<BaseCheckboxInput
					columnAlign="center"
					testID="confirm-bid-consent-checkbox"
					onChange={checked => setConsent(checked)}
				>
					<S.CheckboxTextWrapper>
						{$t<JSX.Element>('rewards.auctions.bids.confirmDrawer.terms', {
							link: chunks => (
								<S.CheckboxText $isLink onClick={() => handlePress(settings.mwsTermsAndConditionsLink)}>
									{chunks}
								</S.CheckboxText>
							),
							privacyPolicy: chunks => (
								<S.CheckboxText $isLink onClick={() => handlePress(settings.privacyPolicyLink)}>
									{chunks}
								</S.CheckboxText>
							)
						})}
					</S.CheckboxTextWrapper>
				</BaseCheckboxInput>
			</S.CheckboxWrapper>
		</GenericDrawer>
	);
};

namespace S {
	export const StyledWarning = styled.div`
		display: flex;
		flex-direction: row;
		background-color: ${({ theme }) => theme.colors.backgrounds.warning.subtle};
		padding: ${({ theme }) => theme.spacings.xxxsmall};
		margin-bottom: ${({ theme }) => theme.spacings.small};
		${({ theme }) => theme.border.radius.medium};
		align-items: center;
	`;

	export const StyledWaringIcon = styled(WarningIcon)`
		margin-right: ${({ theme }) => theme.spacings.xxsmall};
		color: ${({ theme }) => theme.colors.text.main.subtle};
		flex-shrink: 0;
	`;

	export const WarningText = styled.div`
		color: ${({ theme }) => theme.colors.text.main.enabled};
		${({ theme }) => theme.fonts.text.text5};
		flex-shrink: 1;
	`;

	export const TitleWrapper = styled.div`
		display: flex;
		align-items: center;
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const PriceTitle = styled.div<{ $isBold?: boolean }>`
		${({ $isBold, theme }) => theme.fonts.text[$isBold ? 'text1' : 'text3']};
	`;

	export const Price = styled.div`
		${({ theme }) => theme.fonts.text.text1};
		color: ${({ theme }) => theme.colors.text.main.enabled};
		text-align: right;
	`;

	export const PriceChz = styled.div`
		${({ theme }) => theme.fonts.text.caption2};
		color: ${({ theme }) => theme.colors.text.main.enabled};
		text-align: right;
	`;

	export const DescriptionText = styled.div`
		${({ theme }) => theme.fonts.text.text3};
		margin: ${({ theme }) => `${theme.spacings.xxsmall} 0 ${theme.spacings.medium}`};
		text-align: center;
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const DeliveryToText = styled.div`
		${({ theme }) => theme.fonts.text.text3};
		text-align: center;
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const CheckboxWrapper = styled.div`
		display: flex;
		justify-content: center;
		margin: ${({ theme }) => `${theme.spacings.xxxsmall} ${theme.spacings.xlarge} 0`};
		${({ theme }) => theme.breakpoints.queries.below.tabletSmallDesktop} {
			margin: ${({ theme }) => `${theme.spacings.large} 0 0`};
		}
	`;

	export const CheckboxTextWrapper = styled.div`
		${({ theme }) => theme.fonts.text.caption2};
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const CheckboxText = styled.span<{ $isLink?: boolean }>`
		${({ theme }) => theme.fonts.text.caption2};
		color: ${({ $isLink, theme }) => ($isLink ? theme.colors.text.brand.enabled : theme.colors.text.main.enabled)};
		${({ $isLink }) => $isLink && 'cursor: pointer;'}
	`;

	export const TooltipTitle = styled.div`
		${({ theme }) => theme.fonts.text.text8};
		color: ${({ theme }) => theme.colors.text.main.enabled};
		margin-bottom: ${({ theme }) => theme.spacings.xxxsmall};
	`;

	export const TooltipText = styled.div`
		${({ theme }) => theme.fonts.text.text5};
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const StyledRow = styled.div`
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: ${({ theme }) => theme.spacings.xxsmall};
	`;
}
